import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n'
import { Toolbar } from '../components/Toolbar'
import { useBoard } from '../components/useBoard'
import { hexToRgba } from '../lib/board'
import { loadImage } from '../lib/img'
import { strokeQuality } from '../lib/metrics'
import { usePacer } from '../lib/pacer'
import { showResult } from '../lib/flow'
import { useSession, useTools } from '../store/session'
import type { Stroke } from '../lib/types'

const QUOTAS = [8, 5]

export function Exercise() {
  const { t } = useI18n()
  const s = useSession()
  const pacer = usePacer()
  const [step, setStep] = useState(0)
  const [ok, setOk] = useState(0)
  const [fb, setFb] = useState<{ msg: string; ok?: boolean }>({ msg: '' })
  const [busy, setBusy] = useState(false)
  const stepRef = useRef(0)
  stepRef.current = step

  const isFree = s.drawOver
  const quota = QUOTAS[step] ?? 8

  const evalStroke = (st: Stroke) => {
    if (isFree) {
      setFb({ msg: t.exercise.freeAdded })
      useSession.getState().set({ exStrokes: boardRef.current?.strokes ?? [] })
      return
    }
    const q = strokeQuality(st)
    const arc = stepRef.current === 0
    let good = false
    let msg: string

    if (arc) {
      if (q.dur < 450 && q.len < 55) msg = t.exercise.fbLonger
      else if (q.speed > 0.55) msg = t.exercise.fbSlower
      else if (q.sharp > 32) msg = t.exercise.fbSmoother
      else {
        good = true
        msg = usePacer.getState().exhaling ? t.exercise.fbInRhythm : t.exercise.fbGoodNext
      }
      if (!good) st.color = hexToRgba(useTools.getState().color, 0.28)
    } else {
      if (q.dur < 550 && q.len < 70) msg = t.exercise.fbBrushSlower
      else if (q.speed > 0.7) msg = t.exercise.fbBrushCalmer
      else {
        good = true
        msg = t.exercise.fbColorOk
      }
      if (!good) st.color = hexToRgba(useTools.getState().color, 0.12)
    }

    st.ok = good
    boardRef.current?.redraw()
    if (good) {
      setOk((n) => n + 1)
      useSession.getState().set({ calmArcs: useSession.getState().calmArcs + 1 })
    }
    setFb({ msg, ok: good })
    useSession.getState().set({ exStrokes: boardRef.current?.strokes ?? [] })
  }

  const { canvasRef, boardRef } = useBoard({ onStrokeEnd: evalStroke })

  // Подложка: рисунок «до», поверх которого идёт практика.
  useEffect(() => {
    let alive = true
    const b = boardRef.current
    if (!b || !s.beforePNG) return
    void loadImage(s.beforePNG).then((img) => {
      if (alive) {
        b.setBase(img)
        if (s.exStrokes.length && !b.strokes.length) b.load(s.exStrokes)
      }
    })
    return () => { alive = false }
  }, [boardRef, s.beforePNG, s.exStrokes])

  useEffect(() => {
    pacer.start()
    return () => pacer.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Авто-инструмент под шаг: дуги карандашом, цвет — кистью.
  useEffect(() => {
    if (isFree) return
    useTools.getState().set({ tool: step === 0 ? 'pen' : 'brush' })
    setOk(0)
    setFb({ msg: step === 0 ? t.exercise.hintArc : t.exercise.hintColor })
  }, [step, isFree, t])

  const undo = () => {
    const st = boardRef.current?.undo()
    if (st?.ok) {
      setOk((n) => Math.max(0, n - 1))
      useSession.getState().set({ calmArcs: Math.max(0, useSession.getState().calmArcs - 1) })
    }
    setFb({ msg: t.exercise.fbUndone })
    useSession.getState().set({ exStrokes: boardRef.current?.strokes ?? [] })
  }

  const next = async () => {
    const b = boardRef.current
    if (!b || busy) return
    if (!isFree && step < t.exercise.steps.length - 1) {
      setStep(step + 1)
      return
    }
    setBusy(true)
    try {
      useSession.getState().set({ drawOver: false })
      await showResult(b.toDataURL(), b.strokes)
    } finally {
      setBusy(false)
    }
  }

  const stepData = t.exercise.steps[step]
  const focus = step === 0 && s.analysis?.focus ? ` ${t.exercise.focusPrefix}${s.analysis.focus}` : ''
  const canNext = isFree || ok >= quota

  return (
    <section className="screen">
      <h2>{t.exercise.title}</h2>
      <div className="ex-step">
        <b>
          {isFree ? t.exercise.freeTag : `${stepData.tag} · ${step + 1}/${t.exercise.steps.length}`}
        </b>
        <p>{isFree ? t.exercise.freeText : stepData.text + focus}</p>
      </div>
      <div className="canvas-wrap">
        <canvas ref={canvasRef} className="board" />
      </div>
      <Toolbar onUndo={undo} />
      <div className="meter">
        <b>{isFree ? '— / ∞' : `${Math.min(ok, quota)} / ${quota}`}</b>
        <span className={'fb ' + (fb.ok === true ? 'ok' : fb.ok === false ? 'no' : '')}>
          {isFree && !fb.msg ? t.exercise.freeCounted : fb.msg}
        </span>
      </div>
      <div className="btn-row">
        <button className="btn btn-blue" disabled={!canNext || busy} onClick={next}>
          {isFree
            ? t.exercise.done
            : step < t.exercise.steps.length - 1
              ? t.exercise.next
              : t.exercise.finish}
        </button>
      </div>
    </section>
  )
}
